import {
    LexModelsV2Client,
    ListBotsCommand,
    DescribeBotCommand,
    ListBotLocalesCommand,
    ListBotAliasesCommand,
    ListIntentsCommand,
    DescribeIntentCommand,
    ListSlotTypesCommand,
    DescribeSlotTypeCommand,
    ListSlotsCommand,
    DescribeSlotCommand
} from '@aws-sdk/client-lex-models-v2';
import pLimit from 'p-limit';
import { fetchLexV2Analytics, createLexAnalyticsClient } from './lexAnalyticsClient';
import { requestQueueManager } from './requestQueueManager';

// Route every Lex model-building call through the shared rate limiter (the 'lex' service
// bucket) so the parallel fan-out below stays within Lex's throttle quota and benefits from
// the circuit breaker / backoff. The bucket's concurrency — not the code — caps in-flight calls.
const lexSend = (client, command) =>
  requestQueueManager.executeRequest(() => client.send(command), { resourceType: 'lexBotV2' });

const createLexClient = (credentials, region) => new LexModelsV2Client({
  region,
  credentials: {
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey,
    sessionToken: credentials.sessionToken,
  },
  retryMode: 'adaptive', // SDK adaptive retry as a backstop; requestQueueManager is primary pacing
  maxAttempts: 2,
});

export const fetchLexBotsV2 = async (credentials, region) => {
  const client = createLexClient(credentials, region);

  const allBots = [];
  let nextToken = undefined;

  // Step 1: List all bots
  try {
    console.log('Fetching Lex V2 bots...');

    do {
      const command = new ListBotsCommand({
        maxResults: 50,
        nextToken: nextToken,
      });

      const response = await lexSend(client, command);

      if (response.botSummaries) {
        allBots.push(...response.botSummaries);
      }

      nextToken = response.nextToken;
    } while (nextToken);

    console.log(`Found ${allBots.length} Lex V2 bots`);
  } catch (error) {
    console.error('Failed to list Lex V2 bots:', error);
    throw error;
  }

  // Step 2: Get detailed information for each bot, in parallel (bounded). The lex bucket
  // enforces the real concurrency; botLimit just bounds how many bot subtrees fan out at once.
  const botLimit = pLimit(4);
  const detailedBots = await Promise.all(
    allBots.map(bot => botLimit(() => describeBotFull(client, bot)))
  );

  return detailedBots;
};

// Fetch the full detail tree for a single bot. Falls back to the basic summary on failure.
const describeBotFull = async (client, bot) => {
  try {
    const [botDetails, locales, aliases] = await Promise.all([
      lexSend(client, new DescribeBotCommand({ botId: bot.botId })),
      fetchBotLocales(client, bot.botId, bot.botVersion || 'DRAFT'),
      fetchBotAliases(client, bot.botId),
    ]);

    const { $metadata, ...botDetailFields } = botDetails;

    // Detailed locale info (intents + slot types) for all locales in parallel
    const detailedLocales = await fetchDetailedLocaleInfo(client, bot.botId, locales);

    return {
      ...bot,
      ...botDetailFields,
      locales: detailedLocales,
      aliases: aliases,
      childDirected: botDetails.dataPrivacy?.childDirected || false,
    };
  } catch (error) {
    console.error(`Failed to get details for bot ${bot.botId}:`, error);
    // Include basic information even if details fetch fails
    return bot;
  }
};

// Function to fetch conversation analytics for Lex V2 bots
export const enrichLexBotsWithAnalytics = async (credentials, region, bots) => {
  console.log(`Enriching ${bots.length} Lex V2 bots with conversation analytics...`);

  if (!bots || !Array.isArray(bots)) {
    console.warn('No bots provided or bots is not an array');
    return [];
  }

  const emptyAnalytics = () => ({
    totalConversations: 0,
    successfulConversations: 0,
    failedConversations: 0,
    droppedConversations: 0,
    totalUtterances: 0,
    detectedUtterances: 0,
    missedUtterances: 0,
  });

  // Process bots in parallel (bounded). The real cap is the 'lexAnalytics' token bucket in
  // requestQueueManager (concurrency 1, ~1 req/s) — this pLimit just bounds the fan-out that
  // queues against it. A single analytics client is shared across every call.
  const botLimit = pLimit(4);
  const analyticsClient = createLexAnalyticsClient(credentials, region);

  return Promise.all(bots.map(bot => botLimit(async () => {
    try {
      // Validate bot object has required properties
      if (!bot || !bot.botId || !bot.botName) {
        console.warn('Bot missing required properties (botId or botName):', bot);
        return { ...bot, conversationAnalytics: emptyAnalytics() };
      }

      // Collect the bot's real (published) alias IDs. Skip the built-in test alias
      // (TSTALIASID) — it almost always reports zero traffic, so querying it just doubles
      // the analytics call volume. Only fall back to it when there are no real aliases.
      const aliasIds = [];
      if (bot.aliases && Array.isArray(bot.aliases) && bot.aliases.length > 0) {
        bot.aliases.forEach(alias => {
          if (alias.botAliasId && alias.botAliasId !== 'TSTALIASID') aliasIds.push(alias.botAliasId);
        });
      }
      if (aliasIds.length === 0) aliasIds.push('TSTALIASID');

      // Fetch analytics for each alias in parallel and aggregate
      const aggregated = emptyAnalytics();
      const perAlias = await Promise.all(aliasIds.map(async (aliasId) => {
        try {
          return await fetchLexV2Analytics(analyticsClient, bot.botId, aliasId);
        } catch (aliasError) {
          console.warn(`    Failed to fetch analytics for alias ${aliasId}:`, aliasError);
          return null;
        }
      }));

      for (const analytics of perAlias) {
        if (!analytics) continue;
        aggregated.totalConversations += analytics.totalConversations;
        aggregated.successfulConversations += analytics.successfulConversations;
        aggregated.failedConversations += analytics.failedConversations;
        aggregated.droppedConversations += analytics.droppedConversations;
        aggregated.totalUtterances += analytics.totalUtterances;
        aggregated.detectedUtterances += analytics.detectedUtterances;
        aggregated.missedUtterances += analytics.missedUtterances;
      }

      return { ...bot, conversationAnalytics: aggregated };
    } catch (error) {
      console.error(`Failed to fetch analytics for bot ${bot.botName || 'unknown'}:`, error);
      return { ...bot, conversationAnalytics: emptyAnalytics() };
    }
  })));
};

// Helper function to fetch bot locales
const fetchBotLocales = async (client, botId, botVersion) => {
  const locales = [];
  let localesNextToken = undefined;

  try {
    do {
      const localesCommand = new ListBotLocalesCommand({
        botId: botId,
        botVersion: botVersion,
        maxResults: 50,
        nextToken: localesNextToken,
      });

      const localesResponse = await lexSend(client, localesCommand);
      locales.push(...(localesResponse.botLocaleSummaries || []));
      localesNextToken = localesResponse.nextToken;
    } while (localesNextToken);
  } catch (error) {
    console.error(`Failed to fetch locales for bot ${botId}:`, error);
  }

  return locales;
};

// Helper function to fetch bot aliases
const fetchBotAliases = async (client, botId) => {
  const aliases = [];
  let aliasesNextToken = undefined;

  try {
    do {
      const aliasesCommand = new ListBotAliasesCommand({
        botId: botId,
        maxResults: 50,
        nextToken: aliasesNextToken,
      });

      const aliasesResponse = await lexSend(client, aliasesCommand);
      aliases.push(...(aliasesResponse.botAliasSummaries || []));
      aliasesNextToken = aliasesResponse.nextToken;
    } while (aliasesNextToken);
  } catch (error) {
    console.error(`Failed to fetch aliases for bot ${botId}:`, error);
  }

  return aliases;
};

// Helper function to fetch detailed locale information including intents and slot types
const fetchDetailedLocaleInfo = async (client, botId, locales) => {
  return Promise.all(locales.map(async (locale) => {
    try {
      const [intents, slotTypes] = await Promise.all([
        fetchIntentsForLocale(client, botId, locale.botVersion || 'DRAFT', locale.localeId),
        fetchSlotTypesForLocale(client, botId, locale.botVersion || 'DRAFT', locale.localeId),
      ]);

      return {
        ...locale,
        intents: intents,
        slotTypes: slotTypes,
        intentCount: intents.length,
        slotTypeCount: slotTypes.length,
      };
    } catch (error) {
      console.error(`Failed to get detailed info for locale ${locale.localeId}:`, error);
      return { ...locale, intents: [], slotTypes: [], intentCount: 0, slotTypeCount: 0 };
    }
  }));
};

// Helper function to fetch intents for a specific locale
const fetchIntentsForLocale = async (client, botId, botVersion, localeId) => {
  const intents = [];
  let nextToken = undefined;

  try {
    // First, list all intents
    do {
      const listCommand = new ListIntentsCommand({
        botId: botId,
        botVersion: botVersion,
        localeId: localeId,
        maxResults: 50,
        nextToken: nextToken,
      });

      const listResponse = await lexSend(client, listCommand);

      if (listResponse.intentSummaries) {
        intents.push(...listResponse.intentSummaries);
      }

      nextToken = listResponse.nextToken;
    } while (nextToken);

    // Then, get detailed information for each intent in parallel
    return Promise.all(intents.map(async (intent) => {
      try {
        const describeCommand = new DescribeIntentCommand({
          botId: botId,
          botVersion: botVersion,
          localeId: localeId,
          intentId: intent.intentId,
        });

        const [intentDetails, slots] = await Promise.all([
          lexSend(client, describeCommand),
          fetchSlotsForIntent(client, botId, botVersion, localeId, intent.intentId),
        ]);
        const { $metadata, ...intentFields } = intentDetails;

        return { ...intentFields, slots: slots, slotCount: slots.length };
      } catch (error) {
        console.error(`Failed to get details for intent ${intent.intentId}:`, error);
        return { ...intent, slots: [], slotCount: 0 };
      }
    }));
  } catch (error) {
    console.error(`Failed to fetch intents for locale ${localeId}:`, error);
    return [];
  }
};

// Helper function to fetch slot types for a specific locale
const fetchSlotTypesForLocale = async (client, botId, botVersion, localeId) => {
  const slotTypes = [];
  let nextToken = undefined;

  try {
    // First, list all slot types
    do {
      const listCommand = new ListSlotTypesCommand({
        botId: botId,
        botVersion: botVersion,
        localeId: localeId,
        maxResults: 50,
        nextToken: nextToken,
      });

      const listResponse = await lexSend(client, listCommand);

      if (listResponse.slotTypeSummaries) {
        slotTypes.push(...listResponse.slotTypeSummaries);
      }

      nextToken = listResponse.nextToken;
    } while (nextToken);

    // Then, get detailed information for each slot type in parallel
    return Promise.all(slotTypes.map(async (slotType) => {
      try {
        const describeCommand = new DescribeSlotTypeCommand({
          botId: botId,
          botVersion: botVersion,
          localeId: localeId,
          slotTypeId: slotType.slotTypeId,
        });

        const slotTypeDetails = await lexSend(client, describeCommand);
        const { $metadata, ...slotTypeFields } = slotTypeDetails;

        return slotTypeFields;
      } catch (error) {
        console.error(`Failed to get details for slot type ${slotType.slotTypeId}:`, error);
        return slotType;
      }
    }));
  } catch (error) {
    console.error(`Failed to fetch slot types for locale ${localeId}:`, error);
    return [];
  }
};

// Helper function to fetch slots for a specific intent
const fetchSlotsForIntent = async (client, botId, botVersion, localeId, intentId) => {
  const slots = [];
  let nextToken = undefined;

  try {
    // First, list all slots for the intent
    do {
      const listCommand = new ListSlotsCommand({
        botId: botId,
        botVersion: botVersion,
        localeId: localeId,
        intentId: intentId,
        maxResults: 50,
        nextToken: nextToken,
      });

      const listResponse = await lexSend(client, listCommand);

      if (listResponse.slotSummaries) {
        slots.push(...listResponse.slotSummaries);
      }

      nextToken = listResponse.nextToken;
    } while (nextToken);

    // Then, get detailed information for each slot in parallel
    return Promise.all(slots.map(async (slot) => {
      try {
        const describeCommand = new DescribeSlotCommand({
          botId: botId,
          botVersion: botVersion,
          localeId: localeId,
          intentId: intentId,
          slotId: slot.slotId,
        });

        const slotDetails = await lexSend(client, describeCommand);
        const { $metadata, ...slotFields } = slotDetails;

        return slotFields;
      } catch (error) {
        console.error(`Failed to get details for slot ${slot.slotId}:`, error);
        return slot;
      }
    }));
  } catch (error) {
    console.error(`Failed to fetch slots for intent ${intentId}:`, error);
    return [];
  }
};
